import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import DoorCard from '../../../components/door/DoorCard';
import { DoorDetails } from '../../../components/door/DoorDetails';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import TableTab from '../../../components/tabs/table-tab';
import { toast } from "react-toastify";
import { COMMAND_STATE } from '../../../constants/command-status';
import Commands from '../../../constants/commands';
import { DEF_PG_SIZE_SUB_MIN } from '../../../constants/maximum-page-size';
import { HardwareSubCmds } from '../../../constants/sub-commands';
import { useLocale } from '../../../contexts/localeContext';
import { TableContextProvider } from '../../../contexts/table/TableContextProvider';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import readerColumns from '../../../models/ColumnDefinitions/ReaderColumns';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { Door as DoorModel } from '../../../models/Door';
import { ExecuteCommand } from '../../../models/ExecuteCommand';
import { ListData } from '../../../models/ListData';
import { ListQuery } from '../../../models/ListQuery';
import { Lookup } from '../../../models/Lookup';
import LookupTypes from '../../../models/LookupTypes';
import readerSearch from '../../../models/search/ReaderSearch';
import SiteSearch from '../../../models/search/SiteSearch';
import { TabSummary } from '../../../models/TabSummary';
import { getControllerNamesWithPagnation } from '../../../services/controllerService';
import { getDoorById, getDoorNamesWithPagination, storeDoor } from '../../../services/doorService';
import { executeCommandForDevice } from '../../../services/executeCommandService';
import { getReaders, getReadersById } from '../../../services/readerService';
import { getSiteNamesWithPagination } from '../../../services/siteService';
import { clearTerminal } from '../../../services/terminalService';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { appendFilter } from '../../../utilities/filters';
import ReaderAssignModal from '../../../components/door/ReaderAssignModal';
import { MultiSelectOption } from '../../../models/MultiSelectOption';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

// --> variables to update
const allItemsPath = '/access-control/hardware/doors';
const itemPath = '/access-control/hardware/door/';
// <--

const Door: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.settings), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.readers), badge: 0, showInitial: false, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: false, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const site = useSelector((state: IAppState) => state.site.site);
    const [itemId, setItemId] = useState(pageId);
    const initialDoor: DoorModel = {
        id: 0,
        name: '',
        zone: '',
        controller: '',
        zoneId: undefined,
        controllerId: undefined,
        site: '',
        siteId: undefined,
        entryReader: '',
        entryReaderId:  0,
        exitReader: '',
        exitReaderId: 0,
        readers: 0,
        multiTag: undefined,
        zoneByInterleadingZoneId: undefined,
        zoneByInterleadingZoneName: undefined,
        buildingId: undefined,
        floorId: undefined,
        building: undefined,
        floor: undefined,
        description: '',
    }
    const [currentItem, setCurrentItem] = useState<DoorModel>(initialDoor);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [formikContext, setFormikContext] = useState<any>();
    const [showManagedModal, setShowManagedModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const dialog = useDialog();

    const compileLookup = () => {
        const newLookup = [{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.controllerName,
            values: [],
            dataMethod: getControllerNamesWithPagnation,
            searchAccessor: 'devName'
        },];
        return newLookup;
    };

    // --> variables and methods to update

    const setupActiveColumns = (column) => {
        const inactiveColumns = ['site', 'controller', 'door', 'preset'];
        if (inactiveColumns.includes(column.accessor)) {
            return { ...column, active: false };
        }
        return column;
    };

    const removeSearchForDoor = (column) => {
        if (column.accessor === 'door') {
            return (({ searchOnName, lookupName, ...updatedColumn }) => updatedColumn)(column);
        }
        return column;
    };

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context


    /**
     * Content and descriptors for each tab
     */
    const tabContentList: ContentWrapper[] = [{
        content: <DoorDetails />
    },
    {
        content: <TableContextProvider
            dataMethod={(listQuery) => getReaders(translate, { ...listQuery, filters: appendFilter(itemId, "Door", "location.id", listQuery?.filters ?? []) })}
            tableDescriptor={readerColumns.map(setupActiveColumns).map(removeSearchForDoor)}
            searchDescriptor={readerSearch}
            useTable
            usePagination
            useSearch
            useSort
            useFilter
            manualDataRefresh={manualDataRefresh}
            maxPageSize={DEF_PG_SIZE_SUB_MIN}
            lookup={lookup}>
            <TableTab
                title={translate.byKey(TranslationKeys.assignedReaders)}
                selectRows={false}
                cyName="door"
            >
                <button id='manageButton' type="button" className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                    onClick={() => setShowManagedModal(true)}>
                    {translate.byKey(TranslationKeys.manage)}
                </button>
            </TableTab>
        </TableContextProvider>
    },
    {
        content: <Description title={translate.byKey(TranslationKeys.notes)} />
    }];

    const topic: Topic = {
        accessor: 'locName',
        title: translate.byKey(TranslationKeys.doors),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    }

    /**
     * Fetch all the required data on a re-render
     */
    const reloadPage = async () => {
        try {
            if (itemId) {
                const door: DoorModel = await getDoorById(itemId);

                setSelectedItem({label: door.name, value: door.id.toString()})
                setCurrentItem(door);
                // update the tab badge counts
                const updatedTabs = updateTabBadgeTotal(1, door.readers, initialTabs);
                setTabs(updatedTabs);
                setBreadcrumb([
                    { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                    { value: allItemsPath, displayName: translate.byKey(TranslationKeys.doors) },
                    { value: itemPath + `${itemId}`, displayName: `${door.name ?? ''}` }
                ]);
                return;
            } else {
                setCurrentItem(initialDoor);
                setTabs([initialTabs[0]]);
            }

        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    /**
     * Save details when the form is submitted
     * @param values
     */
    const saveDoor = (values) => {
        const door: DoorModel = {
            ...currentItem,
            id: +itemId,
            name: values.name,
            siteId: itemId ? values.siteId : site.value,
            entryReaderId: values.entryReaderId,
            exitReaderId: values.exitReaderId,
            multiTag: values.multiTag,
            zoneId: values.zoneId,
            zoneByInterleadingZoneId: values.zoneByInterleadingZoneId,
            buildingId: values.buildingId,
            floorId: values.floorId,
            site: itemId ? values.site : site.label,
            zone: values.zone,
            entryReader: '',
            exitReader: '',
            controller: values.controller,
            description: values.description,
        };

        const createNewDoor = itemId ? +itemId < 0 : true;
        saveItem(door, createNewDoor);
    }

    // <--

    useEffect(() => {
        setLookup(compileLookup());
    }, [])

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        if(itemId){
            dataRefresh((listQuery) => getReaders(translate, { ...listQuery, filters: appendFilter(itemId, "Door", "location.id", listQuery?.filters ?? []) }));
        }
        reloadPage();
    }, [itemId])

    /**
     *
     * @returns Inner content block
     */
    const getTabContent = (index?: number) => {
        const selectedTabContent = tabContentList[index ?? 0];
        return selectedTabContent.content;
    };

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const saveItem = (item: any, createNewItem: boolean) => {
        setBusy(true);
        storeDoor(item, createNewItem)
            .then(async (value: any) => {
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.doorSaved));
                //Not ideal because itemId will call this but keep it here for now some views need double reload to have correct information.
                reloadPage();
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
                if (formikContext) {
                    formikContext.resetForm();
                }
            })
            .finally(() => {
                setBusy(false);
            });
    }

    const onClearTerminal = async (terminalId) => {
        setBusy(true);
        clearTerminal(terminalId)
            .then((result) => {
                toast.success(translate.byKey(TranslationKeys.readerRemoved));
                reloadPage();
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
            })
            .finally(() => {
                setBusy(false);
            })
    }

    const openDoor = async (entryReader, exitReader) => {
        try {
            const readerId = entryReader && exitReader ? entryReader : exitReader ? exitReader : entryReader;
            const reader = await getReadersById(readerId);

            const command: ExecuteCommand = {
                cmd: Commands.Device.label,
                cmdSubType: HardwareSubCmds.Open_Door,
                userLoginId: user.id,
                fixedAddress: reader.fixedAddress,
                jsonResponse: ""
            }

            executeCommandForDevice(command)
                .then((response: any) => {
                    if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {
                        return toast.success(translate.byKey(TranslationKeys.doorOpened));
                    }
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.cantOpenDoorMessage) });
                })
                .catch((error) => {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
                })
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    return <div className='flex h-full'>
        <TopicContextProvider dataMethod={getDoorNamesWithPagination} itemId={itemId} topic={topic} selectedItem={selectedItem} >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveDoor}
                    tabs={tabs}
                    initialValues={currentItem}
                    itemPath={itemPath}>
                    <DoorCard
                        decoupleReader={onClearTerminal}
                        openDoor={openDoor}
                        setFormikContext={setFormikContext}
                        currentItem={currentItem} />
                    <ReaderAssignModal
                        isOpen={showManagedModal}
                        onClose={() => { setShowManagedModal(false) }}
                        title={translate.byKey(TranslationKeys.assignReaders)}
                        currentItem={currentItem}
                        decoupleReader={onClearTerminal}
                        setFormikContext={setFormikContext}
                        handleOnSubmit={saveDoor} />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
    </div>
}

export default Door;
